import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import type { TierOppgradertHendelse } from '@/state/GameState';
import { animateModalGlide } from '@/ui/modalGlide';
import { autoSpeed } from '@/systems/autoSpeed';

const GULL = 0xb8860b;
const GULL_CSS = '#b8860b';

export class TierCelebrationModal {
  private layer?: Phaser.GameObjects.Container;
  private ko: TierOppgradertHendelse[] = [];
  private aktiv = false;

  constructor(private scene: Phaser.Scene) {}

  vis(hendelse: TierOppgradertHendelse): void {
    this.ko.push(hendelse);
    if (!this.aktiv) this.aapneNeste();
  }

  close(): void {
    this.layer?.destroy(true);
    this.layer = undefined;
    this.aktiv = false;
    this.ko = [];
    autoSpeed.fortsett();
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private aapneNeste(): void {
    const neste = this.ko.shift();
    if (!neste) {
      this.aktiv = false;
      return;
    }
    this.aktiv = true;
    this.render(neste);
  }

  private lukkOgNeste(): void {
    this.layer?.destroy(true);
    this.layer = undefined;
    if (this.ko.length > 0) {
      this.scene.time.delayedCall(150, () => this.aapneNeste());
    } else {
      this.aktiv = false;
      autoSpeed.fortsett();
    }
  }

  private render(hendelse: TierOppgradertHendelse): void {
    this.layer = this.scene.add.container(0, 0).setDepth(900);
    animateModalGlide(this.scene, this.layer, { spillLyd: false });

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    const sheetW = 920;
    const sheetH = 1280;
    const sheetX = (GAME_WIDTH - sheetW) / 2;
    const sheetY = (GAME_HEIGHT - sheetH) / 2;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, sheetW, sheetH, COLORS.wine, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(6, GULL, 0.9);
    this.layer.add(sheet);

    const gullBand = this.scene.add
      .rectangle(sheetX, sheetY + 40, sheetW, 8, GULL, 1)
      .setOrigin(0, 0);
    this.layer.add(gullBand);

    const gullBand2 = this.scene.add
      .rectangle(sheetX, sheetY + sheetH - 48, sheetW, 8, GULL, 1)
      .setOrigin(0, 0);
    this.layer.add(gullBand2);

    const banner = this.scene.add
      .text(GAME_WIDTH / 2, sheetY + 140, 'Tier Up!', {
        fontFamily: FONTS.display,
        fontSize: fontStr(110),
        color: GULL_CSS,
        fontStyle: '700',
      })
      .setOrigin(0.5);
    banner.setAlpha(0);
    this.layer.add(banner);

    this.scene.tweens.add({
      targets: banner,
      alpha: 1,
      y: sheetY + 160,
      duration: 700,
      ease: 'Cubic.easeOut',
    });

    const tittel = this.scene.add
      .text(
        GAME_WIDTH / 2,
        sheetY + 340,
        `Du har nådd Tier ${hendelse.tilTier}`,
        {
          fontFamily: FONTS.display,
          fontSize: fontStr(54),
          color: '#ffffff',
          align: 'center',
        },
      )
      .setOrigin(0.5);
    tittel.setAlpha(0);
    this.layer.add(tittel);

    const navn = this.scene.add
      .text(GAME_WIDTH / 2, sheetY + 430, `«${hendelse.tierNavn}»`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(46),
        color: GULL_CSS,
        fontStyle: 'italic',
        align: 'center',
      })
      .setOrigin(0.5);
    navn.setAlpha(0);
    this.layer.add(navn);

    this.scene.tweens.add({
      targets: [tittel, navn],
      alpha: 1,
      delay: 400,
      duration: 600,
    });

    const fordelOverskrift = this.scene.add
      .text(GAME_WIDTH / 2, sheetY + 530, 'Nye fordeler', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(24),
        color: GULL_CSS,
        fontStyle: '700',
        align: 'center',
      })
      .setOrigin(0.5);
    fordelOverskrift.setAlpha(0);
    this.layer.add(fordelOverskrift);

    const fordelStart = sheetY + 590;
    const fordelTekstObjekter: Phaser.GameObjects.Text[] = [];
    let fy = fordelStart;
    for (const fordel of hendelse.fordeler) {
      const punkt = this.scene.add
        .text(sheetX + 80, fy, `•  ${fordel}`, {
          fontFamily: FONTS.body,
          fontSize: fontStr(28),
          color: '#ffffff',
          wordWrap: { width: sheetW - 160 },
        })
        .setOrigin(0, 0);
      punkt.setAlpha(0);
      this.layer.add(punkt);
      fordelTekstObjekter.push(punkt);
      fy += punkt.height + 14;
    }

    this.scene.tweens.add({
      targets: [fordelOverskrift, ...fordelTekstObjekter],
      alpha: 1,
      delay: 700,
      duration: 600,
    });

    const knappY = sheetY + sheetH - 160;
    const knappW = 320;
    const knappH = 80;
    const knappX = (GAME_WIDTH - knappW) / 2;

    const knappBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, GULL, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.white, 0.9);
    knappBg.setAlpha(0);

    const knappTxt = this.scene.add
      .text(GAME_WIDTH / 2, knappY + knappH / 2, 'Fortsett →', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(30),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    knappTxt.setAlpha(0);

    this.layer.add([knappBg, knappTxt]);

    this.scene.tweens.add({
      targets: [knappBg, knappTxt],
      alpha: 1,
      delay: 1100,
      duration: 400,
      onComplete: () => {
        knappBg.setInteractive({ useHandCursor: true });
        knappBg.on('pointerover', () => knappBg.setFillStyle(0xd9a14b));
        knappBg.on('pointerout', () => knappBg.setFillStyle(GULL));
        knappBg.on('pointerup', () => this.lukkOgNeste());
      },
    });
  }
}
